
import { API_BASE_URL } from '../constants';
import { auth } from '../firebase';
import { MockupPosition } from '../types';
import { logger } from '../utils/logger';

const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const handleResponse = async (res: Response, endpoint: string) => {
  if (!res.ok) {
    const text = await res.text();
    let json;
    try {
      json = JSON.parse(text);
    } catch (e) {
      logger.error(`API Error at ${endpoint}:`, { status: res.status, text });
      // If 404 and HTML, it usually means wrong base URL
      if (res.status === 404 && text.includes('<!DOCTYPE html>')) {
         throw new Error(`Connection Error: Could not find backend at ${API_BASE_URL}. Please check VITE_API_URL configuration.`);
      }
      throw new Error(`Server Error (${res.status}). The server returned an unexpected response.`);
    }
    logger.warn(`API non-ok response at ${endpoint}:`, { status: res.status, error: json.error });
    throw new Error(json.error || `API Error: ${res.status}`);
  }
  return res.json();
};

export const apiService = {
  cloneDesign: async (imageData: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/clone-design`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageData })
    });
    return handleResponse(res, '/api/clone-design');
  },

  redesign: async (imageData: string, generalInstruction: string, redInstruction: string, greenInstruction: string, maskData: any) => {
    const headers = await getAuthHeaders();
    const payload: any = { 
        imageData, 
        generalInstruction, 
        redInstruction,
        greenInstruction
    };
    
    if (maskData && typeof maskData === 'object') {
        // Send the combined colored mask for Step 1
        payload.maskData = maskData.combinedMask;
    } else {
        payload.maskData = null;
    }

    const res = await fetch(`${API_BASE_URL}/api/redesign`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    return handleResponse(res, '/api/redesign');
  },

  embroidery: async (imageData: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/embroidery`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageData })
    });
    return handleResponse(res, '/api/embroidery');
  },

  removeBackground: async (imageData: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/remove-background`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageData })
    });
    return handleResponse(res, '/api/remove-background');
  },

  enhanceProduct: async (imageData: string, prompt: string, productType: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/enhance-product`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageData, prompt, productType })
    });
    return handleResponse(res, '/api/enhance-product');
  },
  
  // New Enhancer Prompt Methods
  generateEnhancerPrompt: async (productName: string, instructions: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/generate-enhancer-prompt`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ productName, instructions })
    });
    return handleResponse(res, '/api/generate-enhancer-prompt');
  },

  getEnhancerPrompts: async () => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/enhancer-prompts`, {
      method: 'GET',
      headers
    });
    return handleResponse(res, '/api/enhancer-prompts');
  },

  saveEnhancerPrompt: async (productType: string, name: string, prompt: string, comment: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/enhancer-prompts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ productType, name, prompt, comment })
    });
    return handleResponse(res, '/api/enhancer-prompts');
  },

  updateEnhancerPrompt: async (id: string, name: string, prompt: string, comment: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/enhancer-prompts/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ name, prompt, comment })
    });
    return handleResponse(res, `/api/enhancer-prompts/${id}`);
  },

  deleteEnhancerPrompt: async (id: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/enhancer-prompts/${id}`, {
      method: 'DELETE',
      headers
    });
    return handleResponse(res, `/api/enhancer-prompts/${id}`);
  },

  shareEnhancerPrompt: async (id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/enhancer-prompts/${id}/share`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/enhancer-prompts/${id}/share`);
  },

  revokeShareEnhancerPrompt: async (id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/enhancer-prompts/${id}/revoke`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/enhancer-prompts/${id}/revoke`);
  },

  mockup: async (
    mockupDataArray: string[], 
    designData: string, 
    dimensionsArray: any[], 
    skuName: string, 
    designType: string, 
    position: MockupPosition,
    placementMode?: 'single' | 'dual',
    designDataBack?: string | null,
    positionBack?: MockupPosition
  ) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/mockup`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        mockupDataArray,
        designData,
        dimensionsArray,
        skuName,
        designType,
        position,
        placementMode,
        designDataBack,
        positionBack
      })
    });
    return handleResponse(res, '/api/mockup');
  },

  upscale: async (imageData: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/upscale`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageData })
    });
    return handleResponse(res, '/api/upscale');
  },

  // Listing Methods
  optimizeListing: async (data: { title: string; description: string; tags: string; imageUrl?: string; imageData?: string; skipHistory?: boolean; additionalContext?: string; language?: string }) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/optimize-listing`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    return handleResponse(res, '/api/optimize-listing');
  },

  // Listing Context Methods
  getListingContexts: async () => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/listing-contexts`, { method: 'GET', headers });
    return handleResponse(res, '/api/listing-contexts');
  },

  saveListingContext: async (name: string, content: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/listing-contexts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name, content })
    });
    return handleResponse(res, '/api/listing-contexts');
  },

  deleteListingContext: async (id: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/listing-contexts/${id}`, { method: 'DELETE', headers });
    return handleResponse(res, `/api/listing-contexts/${id}`);
  },

  shareListingContext: async (id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/listing-contexts/${id}/share`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/listing-contexts/${id}/share`);
  },

  revokeShareListingContext: async (id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/listing-contexts/${id}/revoke`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/listing-contexts/${id}/revoke`);
  },

  startVideoGeneration: async (data: { referenceName: string; videoPrompt: string; imageUrl: string; listingData?: any }) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/generate-video`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    return handleResponse(res, '/api/generate-video');
  },

  checkVideoStatus: async (jobId: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/check-video-status`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ jobId })
    });
    return handleResponse(res, '/api/check-video-status');
  },

  // History Methods
  getFullHistory: async () => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/full-history`, {
      method: 'GET',
      headers
    });
    return handleResponse(res, '/api/full-history');
  },

  deleteHistoryItem: async (type: string, id: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/history/${type}/${id}`, {
      method: 'DELETE',
      headers
    });
    return handleResponse(res, `/api/history/${type}/${id}`);
  },

  toggleShareHistoryItem: async (type: string, id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/history/${type}/${id}/share`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/history/${type}/${id}/share`);
  },

  revokeShareHistoryItem: async (type: string, id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/history/${type}/${id}/revoke`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/history/${type}/${id}/revoke`);
  },

  getSharedItem: async (type: string, id: string) => {
    // Public endpoint, no auth headers needed
    const res = await fetch(`${API_BASE_URL}/api/shared/${type}/${id}`, {
      method: 'GET'
    });
    return handleResponse(res, `/api/shared/${type}/${id}`);
  },

  createCheckout: async (userId: string, packageType: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/payments/create-checkout`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, packageType })
    });
    return handleResponse(res, '/payments/create-checkout');
  },

  // Mockup Templates
  saveMockupTemplate: async (imageData: string, name: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/mockup-templates`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageData, name })
    });
    return handleResponse(res, '/api/mockup-templates');
  },

  getMockupTemplates: async () => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/mockup-templates`, {
      method: 'GET',
      headers
    });
    return handleResponse(res, '/api/mockup-templates');
  },

  deleteMockupTemplate: async (id: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/mockup-templates/${id}`, {
      method: 'DELETE',
      headers
    });
    return handleResponse(res, `/api/mockup-templates/${id}`);
  },

  shareMockupTemplate: async (id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/mockup-templates/${id}/share`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/mockup-templates/${id}/share`);
  },

  revokeShareMockupTemplate: async (id: string, email: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/mockup-templates/${id}/revoke`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });
    return handleResponse(res, `/api/mockup-templates/${id}/revoke`);
  },

  // Admin Methods
  getAdminUsers: async () => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
      method: 'GET',
      headers
    });
    return handleResponse(res, '/api/admin/users');
  },

  updateAdminCredits: async (userId: string, amount: number, description: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/admin/update-credits`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, amount, description })
    });
    return handleResponse(res, '/api/admin/update-credits');
  },

  triggerCleanup: async (maxDeletes: number) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/admin/cleanup-veo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ maxDeletes })
    });
    return handleResponse(res, '/api/admin/cleanup-veo');
  }
};
